import { FC, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Stack,
  styled,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { DateTime } from "luxon";
import { Menstruation } from "../../types";
import {
  calcInclusiveDurationInDaysDisplay,
  isSameDayOrBefore,
} from "../../helpers";

const MenstruationFormControl = styled(FormControl)({
  marginTop: "1rem",
});

const Spacer = styled("div")({
  flexGrow: 1,
});

interface MenstruationDialogProps {
  providedMenstruation?: Menstruation;
  addMenstruation?: (newMenstruation: {
    startDate: DateTime;
    endDate: DateTime;
  }) => void;
  editMenstruation?: (
    menstruationId: number,
    newMenstruation: {
      startDate: DateTime;
      endDate: DateTime;
    },
  ) => void;
  deleteMenstruation?: (menstruationId: number) => void;
  handleClose: () => void;
  open: boolean;
  variant: "add" | "edit";
}

const MenstruationDialog: FC<MenstruationDialogProps> = ({
  providedMenstruation,
  addMenstruation,
  editMenstruation,
  deleteMenstruation,
  handleClose,
  open,
  variant,
}) => {
  const [startDate, setStartDate] = useState<DateTime | null>(
    providedMenstruation?.startDate
      ? DateTime.fromISO(providedMenstruation.startDate)
      : DateTime.local(),
  );
  const [endDate, setEndDate] = useState<DateTime | null>(
    providedMenstruation?.endDate
      ? DateTime.fromISO(providedMenstruation.endDate)
      : DateTime.local(),
  );

  const dialogTitle =
    variant === "add" ? "Add menstruation" : "Edit menstruation";
  const primaryButtonLabel = variant === "add" ? "Add" : "Update";

  const invalidDates =
    startDate !== null &&
    endDate !== null &&
    !isSameDayOrBefore(startDate, endDate);

  const isInvalid =
    startDate === null ||
    endDate === null ||
    !isSameDayOrBefore(startDate, endDate);

  const handleAdd = () => {
    addMenstruation!({ startDate: startDate!, endDate: endDate! });
    handleClose();
  };

  const handleDelete = () => {
    deleteMenstruation!(providedMenstruation!.menstruationId);
    handleClose();
  };

  const handleEdit = () => {
    editMenstruation!(providedMenstruation!.menstruationId, {
      startDate: startDate!,
      endDate: endDate!,
    });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <MenstruationFormControl>
          <Stack spacing={2}>
            {invalidDates && (
              <Alert severity="error">
                End date must not be before start date
              </Alert>
            )}
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <Stack direction="row" spacing={2}>
                <DatePicker
                  label="Start date"
                  value={startDate}
                  onChange={(newStartDate) => setStartDate(newStartDate)}
                />
                <DatePicker
                  label="End date"
                  value={endDate}
                  onChange={(newEndDate) => setEndDate(newEndDate)}
                />
              </Stack>
            </LocalizationProvider>
          </Stack>
          {startDate && endDate && (
            <FormHelperText>
              Duration:{" "}
              {calcInclusiveDurationInDaysDisplay(
                startDate.toISO()!,
                endDate.toISO()!,
              )}
            </FormHelperText>
          )}
        </MenstruationFormControl>
      </DialogContent>
      <DialogActions>
        {variant === "edit" && (
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        )}
        <Spacer />
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={variant === "add" ? handleAdd : handleEdit}
          variant="contained"
          disableElevation
          disabled={isInvalid}
        >
          {primaryButtonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MenstruationDialog;
